import React, { PureComponent } from 'react';
import { Mutation } from 'react-apollo';
import { loader } from 'graphql.macro';

// import config
import { THANK_YOU_PAGE_URL } from '../../../util/config';

// import validators
import { alphaNumericValidator, dropdownValidator, emailValidator, telValidator } from '../../../util/validators';

// import data
import { ctaBarFormData } from './data';

// import styled components
import { Spinner } from '../../styled';
import { Field, Label, Select } from '../styled';
import {
  CtaBarError,
  CtaBarFormBody,
  CtaBarFormFields,
  CtaBarFormFooter,
  CtaBarFormHeader,
  CtaBarFormNotes,
  CtaBarFormWrapper,
  CtaBarInput,
  CtaBarSubmit,
  CtaBarSuccessWrapper,
} from './styled';

// load graphql mutation
const CREATE_LOANFORCE_CONTACT_MUTATION = loader('../../../graphql/CREATE_LOANFORCE_CONTACT_MUTATION.graphql');

class CtaBarForm extends PureComponent {
  constructor() {
    super();

    this.alphaNumericValidator = alphaNumericValidator.bind(this);
    this.dropdownValidator = dropdownValidator.bind(this);
    this.emailValidator = emailValidator.bind(this);
    this.telValidator = telValidator.bind(this);
  }

  state = {
    values: { firstName: '', lastName: '', phone: '', email: '', purpose: '', notes: '' },
    errors: { firstName: '', lastName: '', phone: '', email: '', purpose: '' },
    submitErrors: { firstName: '', lastName: '', phone: '', email: '', purpose: '' },
    formIsValid: false,
  };

  componentDidMount() {
    const submitErrors = { ...this.state.submitErrors };
    const submitKeys = Object.keys(submitErrors).map(key => {
      return key;
    });

    ctaBarFormData.map(field => {
      submitKeys.map(key => {
        if (field.name === key) {
          submitErrors[key] = field.submitError;
        }
        return null;
      });
      return null;
    });

    this.setState({
      submitErrors,
    });
  }

  drawFields = fields => {
    return fields.map(field => {
      const { name, type, label, validators } = field;

      if (type === 'select') {
        return (
          <Field key={name}>
            <Select
              name={name}
              aria-labelledby={name}
              id={name}
              value={this.state.values[name]}
              onChange={e => this.handleChange(e, validators)}
            >
              <option value="">Loan Purpose</option>
              <option value="purchase">Purchase</option>
              <option value="refinance">Refinance</option>
            </Select>
            {this.state.errors[name] ? (
              <CtaBarError className="error">{this.state.errors[name]}</CtaBarError>
            ) : (
              <CtaBarError>&nbsp;</CtaBarError>
            )}
          </Field>
        );
      }

      return (
        <Field key={name}>
          <CtaBarInput
            type={type}
            aria-labelledby={name}
            name={name}
            id={name}
            value={this.state.values[name]}
            onChange={e => this.handleChange(e, validators)}
            required
          />
          <Label htmlFor={name} color="white">
            {label}
          </Label>
          {this.state.errors[name] ? (
            <CtaBarError className="error">{this.state.errors[name]}</CtaBarError>
          ) : (
            <CtaBarError>&nbsp;</CtaBarError>
          )}
        </Field>
      );
    });
  };

  handleChange = (e, validators = []) => {
    const { name, value } = e.target;

    const updateObj = { ...this.state.values };

    updateObj[name] = value;

    validators.forEach(validator => {
      this[validator](value, name);
    });

    this.setState({ values: updateObj });
  };

  checkIfValid = async () => {
    const { values, errors, submitErrors } = this.state;

    let isValid = true;

    await Object.keys(values).map(key => {
      if (key === 'notes') {
        return null;
      }

      if (values[key].length === 0) {
        isValid = false;
        errors[key] = submitErrors[key];
      }
      return null;
    });

    if (isValid) {
      this.setState({
        formIsValid: true,
      });
    } else {
      this.setState({ errors });
    }
  };

  render() {
    const { firstName, lastName, email, phone } = this.state.values;

    return (
      <CtaBarFormWrapper>
        <CtaBarFormHeader>
          <h2>Request a Consultation</h2>
          <span onClick={() => this.props.hideModal()}>╳</span>
        </CtaBarFormHeader>
        <CtaBarFormBody>
          <Mutation mutation={CREATE_LOANFORCE_CONTACT_MUTATION}>
            {(createLoanForceContact, { data, error, loading }) => {
              if (data) {
                setTimeout(() => {
                  window.location.href = THANK_YOU_PAGE_URL;
                }, 500);

                return (
                  <CtaBarSuccessWrapper>
                    <h3>Thank you for your request!</h3>
                    <p>We will contact you shortly.</p>
                  </CtaBarSuccessWrapper>
                );
              }

              if (loading) {
                return (
                  <CtaBarSuccessWrapper>
                    <Spinner />
                  </CtaBarSuccessWrapper>
                );
              }

              if (error) {
                return (
                  <CtaBarSuccessWrapper>
                    <h3>We're sorry!</h3>
                    <p>An error was encountered with your submission - please refresh the page and try again.</p>
                  </CtaBarSuccessWrapper>
                );
              }

              return (
                <>
                  <CtaBarFormFields>{this.drawFields(ctaBarFormData)}</CtaBarFormFields>
                  <CtaBarFormNotes>
                    <textarea
                      name="notes"
                      id="notes"
                      aria-labelledby="notes"
                      placeholder="Notes for the Request"
                      value={this.state.values.notes}
                      onChange={e => this.handleChange(e)}
                    />
                  </CtaBarFormNotes>
                  <CtaBarSubmit
                    onClick={async () => {
                      const branchSource = `Branch Website - Get a Quote - ${this.props.name}`;

                      await this.checkIfValid();

                      if (this.state.formIsValid) {
                        await createLoanForceContact({
                          variables: {
                            firstName,
                            lastName,
                            email,
                            phone,
                            campaignName: 'OTHL - Branch Website - Get a Quote',
                            campaignStatus: 'Active',
                            source: branchSource,
                            userAccountName: this.props.account,
                          },
                        });
                      }

                      return null;
                    }}
                  >
                    Submit Request
                  </CtaBarSubmit>
                </>
              );
            }}
          </Mutation>
        </CtaBarFormBody>
        <CtaBarFormFooter>
          <p>
            By clicking the ‘Submit Request’ button above, I authorize OneTrust Home Loans to contact me using an
            automated dialing system, even if the telephone number is a cellular phone or other service for which the
            party is charged. I understand that I am not required to provide this consent in order to obtain goods or
            services from OneTrust Home Loans. I agree to receive disclosures and communications in electronic form and
            I con rm that I have a working computer system and an internet website browser to view this webpage and this
            loan inquiry form. I agree that OneTrust Home Loans may contact me by mail, telephone (mobile devices
            including SMS and MMS) or email in connection with my request even if my telephone number or email address
            appears on a OneTrust Home Loans internal Do Not Call / Do Not Email List, a State or National Do Not Call
            Registry, a Do Not E-Mail list, or any other Do Not Contact List.
          </p>
        </CtaBarFormFooter>
      </CtaBarFormWrapper>
    );
  }
}

export default CtaBarForm;
